import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { DayRange } from '../../lib/format/dates';
import {
  getAvgOrderValue,
  getChefActivityTrends,
  getCohortRetention,
  getDishSeries,
  getFunnel,
  getListingOutcomes,
  getMostViewedCuisines,
  getMostViewedDishes,
  getRepeatPurchaseRate,
  getSignupSeries,
  getSnapshot,
  getTopOrderedCuisines,
  getTopPerformers,
  listLocaleActivity,
  type AnalyticsWindow,
  type Bucket,
  type LocaleActivityParams,
} from './api';

/**
 * Every dashboard card shows its own loading / error states, so all of
 * these are `silent` — one failed tile must not raise a modal over the rest.
 */
export const analyticsKeys = {
  all: ['analytics'] as const,
  snapshot: (localeIds: string[]) => [...analyticsKeys.all, 'snapshot', localeIds] as const,
  locales: (p: LocaleActivityParams) => [...analyticsKeys.all, 'locales', p] as const,
  signups: (type: string, w: AnalyticsWindow) => [...analyticsKeys.all, 'signups', type, w] as const,
  dishes: (event: string, w: AnalyticsWindow) => [...analyticsKeys.all, 'dishes', event, w] as const,
  funnel: (w: AnalyticsWindow) => [...analyticsKeys.all, 'funnel', w] as const,
  cohorts: (type: string, cb: Bucket, w: AnalyticsWindow) => [...analyticsKeys.all, 'cohorts', type, cb, w] as const,
  chefTrends: (w: AnalyticsWindow) => [...analyticsKeys.all, 'chef-trends', w] as const,
  topPerformers: (w: AnalyticsWindow) => [...analyticsKeys.all, 'top-performers', w] as const,
  repeat: (w: AnalyticsWindow) => [...analyticsKeys.all, 'repeat', w] as const,
  aov: (w: AnalyticsWindow) => [...analyticsKeys.all, 'aov', w] as const,
  orderedCuisines: (w: AnalyticsWindow) => [...analyticsKeys.all, 'ordered-cuisines', w] as const,
  viewedDishes: (w: AnalyticsWindow) => [...analyticsKeys.all, 'viewed-dishes', w] as const,
  viewedCuisines: (w: AnalyticsWindow) => [...analyticsKeys.all, 'viewed-cuisines', w] as const,
  listingOutcomes: (w: AnalyticsWindow) => [...analyticsKeys.all, 'listing-outcomes', w] as const,
};

const base = { staleTime: 60_000, placeholderData: keepPreviousData, meta: { silent: true } } as const;

export const useSnapshot = (localeIds: string[], days: DayRange) =>
  useQuery({
    ...base,
    queryKey: [...analyticsKeys.snapshot(localeIds), days] as const,
    queryFn: ({ signal }) => getSnapshot(localeIds, days, signal),
    placeholderData: keepPreviousData,
  });

export const useLocaleActivity = (p: LocaleActivityParams) =>
  useQuery({ ...base, queryKey: analyticsKeys.locales(p), queryFn: ({ signal }) => listLocaleActivity(p, signal) });

export const useSignupSeries = (type: 'chef' | 'eater', w: AnalyticsWindow) =>
  useQuery({
    ...base,
    queryKey: analyticsKeys.signups(type, w),
    queryFn: ({ signal }) => getSignupSeries(type, w, signal),
  });

export const useDishSeries = (event: 'listed' | 'sold', w: AnalyticsWindow) =>
  useQuery({
    ...base,
    queryKey: analyticsKeys.dishes(event, w),
    queryFn: ({ signal }) => getDishSeries(event, w, signal),
  });

export const useFunnel = (w: AnalyticsWindow) =>
  useQuery({ ...base, queryKey: analyticsKeys.funnel(w), queryFn: ({ signal }) => getFunnel(w, signal) });

export const useCohortRetention = (type: 'chef' | 'eater', cohortBucket: Bucket, w: AnalyticsWindow) =>
  useQuery({
    ...base,
    queryKey: analyticsKeys.cohorts(type, cohortBucket, w),
    queryFn: ({ signal }) => getCohortRetention(type, cohortBucket, w, signal),
  });

export const useChefActivityTrends = (w: AnalyticsWindow) =>
  useQuery({
    ...base,
    queryKey: analyticsKeys.chefTrends(w),
    queryFn: ({ signal }) => getChefActivityTrends(w, 4, signal),
  });

export const useTopPerformers = (w: AnalyticsWindow) =>
  useQuery({
    ...base,
    queryKey: analyticsKeys.topPerformers(w),
    queryFn: ({ signal }) => getTopPerformers(w, 10, signal),
  });

export const useRepeatPurchaseRate = (w: AnalyticsWindow) =>
  useQuery({ ...base, queryKey: analyticsKeys.repeat(w), queryFn: ({ signal }) => getRepeatPurchaseRate(w, signal) });

export const useAvgOrderValue = (w: AnalyticsWindow) =>
  useQuery({ ...base, queryKey: analyticsKeys.aov(w), queryFn: ({ signal }) => getAvgOrderValue(w, signal) });

export const useTopOrderedCuisines = (w: AnalyticsWindow) =>
  useQuery({
    ...base,
    queryKey: analyticsKeys.orderedCuisines(w),
    queryFn: ({ signal }) => getTopOrderedCuisines(w, 8, signal),
  });

export const useMostViewedDishes = (w: AnalyticsWindow) =>
  useQuery({
    ...base,
    queryKey: analyticsKeys.viewedDishes(w),
    queryFn: ({ signal }) => getMostViewedDishes(w, 8, signal),
  });

export const useMostViewedCuisines = (w: AnalyticsWindow) =>
  useQuery({
    ...base,
    queryKey: analyticsKeys.viewedCuisines(w),
    queryFn: ({ signal }) => getMostViewedCuisines(w, 8, signal),
  });

export const useListingOutcomes = (w: AnalyticsWindow) =>
  useQuery({
    ...base,
    queryKey: analyticsKeys.listingOutcomes(w),
    queryFn: ({ signal }) => getListingOutcomes(w, signal),
  });
